import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { locales } from '@soroush/shared/locales';
import { Button, Input } from '@soroush/ui';
import { BrandLogo } from '../components/BrandLogo';
import { ApiError, apiFetch, setTokens } from '../lib/api';
import { isLatinName, isValidMobile } from '../lib/validation';

function emailOk(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function passwordOk(value: string): boolean {
  return (
    value.length >= 10 &&
    /[A-Z]/.test(value) &&
    /[a-z]/.test(value) &&
    /\d/.test(value) &&
    /[^A-Za-z0-9]/.test(value)
  );
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && Array.isArray(err.details)) {
    const first = (err.details as { message?: string }[])[0]?.message;
    if (first) return first;
  }
  return err instanceof Error ? err.message : fallback;
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [identifier, setIdentifier] = useState('');
  const [altContact, setAltContact] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const primaryIsEmail = identifier.includes('@');
  const primaryIsMobile = !primaryIsEmail && isValidMobile(identifier);
  const primaryReady = primaryIsEmail ? emailOk(identifier) : primaryIsMobile;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const id = identifier.trim();
    if (primaryIsEmail ? !emailOk(id) : !isValidMobile(id)) {
      setError(t('auth.invalidIdentifier'));
      return;
    }
    if (mode === 'register') {
      if (!isLatinName(firstName) || !isLatinName(lastName)) {
        setError(t('common.latinOnly'));
        return;
      }
      if (!passwordOk(password)) {
        setError(t('auth.passwordHint'));
        return;
      }
      if (altContact.trim()) {
        const altOk = primaryIsEmail ? isValidMobile(altContact) : emailOk(altContact);
        if (!altOk) {
          setError(primaryIsEmail ? t('common.invalidMobile') : t('auth.invalidIdentifier'));
          return;
        }
      }
    }
    setLoading(true);
    try {
      const path = mode === 'login' ? '/api/v1/auth/login' : '/api/v1/auth/register';
      const email = primaryIsEmail ? id : altContact.trim() || undefined;
      const phone = primaryIsMobile ? id : altContact.trim() || undefined;
      const body =
        mode === 'login'
          ? { email: id, password }
          : {
              email: email || undefined,
              phone: phone || undefined,
              password,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
            };
      const data = await apiFetch<{ accessToken: string; refreshToken: string }>(path, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setTokens(data.accessToken, data.refreshToken);
      navigate('/miles');
    } catch (err) {
      setError(errorText(err, t('common.errorGeneric')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="auth-screen">
      <section className="auth-screen__form">
        <div className="auth-screen__top">
          <BrandLogo variant="wordmark" size="md" to="/" />
          <div className="sa-lang-switch" aria-label={t('common.language')}>
            {locales.map((locale) => (
              <button
                key={locale}
                type="button"
                aria-pressed={i18n.language.startsWith(locale)}
                onClick={() => void i18n.changeLanguage(locale)}
              >
                {locale === 'fa' ? 'فا' : 'EN'}
              </button>
            ))}
          </div>
        </div>

        <div className="auth-screen__body">
          <h1 className="auth-screen__title">
            {mode === 'login' ? t('common.login') : t('common.register')}
          </h1>

          <form onSubmit={(e) => void onSubmit(e)}>
            {mode === 'register' ? (
              <>
                <Input
                  label={t('auth.firstName')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  required
                />
                <Input
                  label={t('common.lastName')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
                <p className="auth-screen__hint">{t('auth.latinHint')}</p>
              </>
            ) : null}
            <Input
              label={t('auth.identifier')}
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              inputMode="text"
              dir="ltr"
              required
            />
            {mode === 'register' && primaryReady ? (
              <>
                <Input
                  label={primaryIsEmail ? t('auth.altMobile') : t('auth.altEmail')}
                  type={primaryIsEmail ? 'tel' : 'email'}
                  value={altContact}
                  onChange={(e) => setAltContact(e.target.value)}
                  autoComplete={primaryIsEmail ? 'tel' : 'email'}
                  dir="ltr"
                />
                <p className="auth-screen__hint">{t('auth.otherContact')}</p>
              </>
            ) : null}
            <Input
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={mode === 'register' ? 10 : 1}
            />
            {mode === 'register' ? <p className="auth-screen__hint">{t('auth.passwordHint')}</p> : null}
            {error ? <div className="sa-alert sa-alert--error">{error}</div> : null}
            <Button type="submit" disabled={loading}>
              {loading ? t('common.loading') : mode === 'login' ? t('auth.signIn') : t('auth.join')}
            </Button>
          </form>

          <p className="auth-screen__switch">
            <span>{mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}</span>
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError('');
                setAltContact('');
              }}
            >
              {mode === 'login' ? t('common.register') : t('auth.signIn')}
            </button>
          </p>
        </div>
      </section>

      <aside className="auth-screen__hero">
        <div className="auth-screen__blob auth-screen__blob--a" />
        <div className="auth-screen__blob auth-screen__blob--b" />
        <div className="auth-screen__blob auth-screen__blob--c" />
        <div className="auth-screen__dots" />
        <div className="auth-screen__hero-copy">
          <h2>{t('auth.welcome')}</h2>
          <p>{t('auth.welcomeLead')}</p>
        </div>
      </aside>
    </main>
  );
}
